import {includes} from "lodash";

// from libs/sport-shared/big9/types/betTypes.ts
export type Big9 = "BIG9";
export type Daily5 = "DAILY5";

/**
 * - scheduled: hasClosed: false, canHaveResults: false, hasPool: false, couponAllowed: false  |future race is scheduled but not yet open for betting
 * - bettable: hasClosed: false, canHaveResults: false, hasPool: true, couponAllowed: true
 * - upcoming: hasClosed: false, canHaveResults: false, hasPool: false, couponAllowed: false  |bet placement is closed race is about to start
 * - ongoing: hasClosed: true, canHaveResults: true, hasPool: true, couponAllowed: false
 * - results: hasClosed: true, canHaveResults: true, hasPool: true, couponAllowed: false
 * - cancelled: hasClosed: true, canHaveResults: false, hasPool: true, couponAllowed: false
 */
export type GameStatus =
    | "scheduled"
    | "upcoming"
    | "bettable"
    | "starting"
    | "ongoing"
    | "results"
    | "settled"
    | "cancelled";

export type GameSport = "trot" | "gallop" | string | undefined;

export type HeadToHeadOfferingType = "H2H";

export type GameType =
    | "V85"
    | "V86"
    | "V75"
    | "V65"
    | "V64"
    | "V5"
    | "V4"
    | "V3"
    | "dd"
    | "ld"
    | "trio"
    | "tvilling"
    | "komb"
    | "vinnare"
    | "plats"
    | "vp"
    | "top7"
    | "GS75"
    | "raket";

export type VXYGameType =
    | "V85"
    | "V75"
    | "V86"
    | "GS75"
    | "V65"
    | "V64"
    | "V5"
    | "V4"
    | "V3";

export type DubbelGameType = "ld" | "dd";

export type ComboGameType = "dd" | "ld" | "trio" | "komb" | "tvilling";

export type NonComboGameType = Exclude<GameType, ComboGameType>;

export type SingleRaceGameType =
    | "trio"
    | "tvilling"
    | "komb"
    | "vinnare"
    | "plats"
    | "vp"
    | "top7";

export type DivisionGameType =
    | "V85"
    | "V75"
    | "V86"
    | "GS75"
    | "V64"
    | "V65"
    | "V5"
    | "V4"
    | "V3"
    | "dd"
    | "ld";

export type SharedBetGameType =
    | "V85"
    | "V75"
    | "V86"
    | "GS75"
    | "V64"
    | "V65"
    | "V5"
    | "V4"
    | "dd"
    | "ld"
    | "trio"
    | "komb"
    | "tvilling"
    | "vinnare"
    | "plats"
    | "top7";

// TODO: consolidate below with atg-horse-game/domain/gameDefs.js?
export enum GameTypes {
    V85 = "V85",
    V75 = "V75",
    V86 = "V86",
    GS75 = "GS75",
    V64 = "V64",
    V65 = "V65",
    V5 = "V5",
    V4 = "V4",
    V3 = "V3",
    dd = "dd",
    ld = "ld",
    trio = "trio",
    komb = "komb",
    tvilling = "tvilling",
    vinnare = "vinnare",
    plats = "plats",
    raket = "raket",
    vp = "vp",
    top7 = "top7",
}

export const {
    V85,
    V75,
    V86,
    GS75,
    V64,
    V65,
    V5,
    V4,
    V3,
    dd,
    ld,
    trio,
    komb,
    tvilling,
    vinnare,
    plats,
    raket,
    vp,
    top7,
} = GameTypes;

export const ALL_GAMES = [
    V85,
    V75,
    V86,
    GS75,
    V64,
    V65,
    V5,
    V4,
    V3,
    dd,
    ld,
    trio,
    komb,
    tvilling,
    vinnare,
    plats,
    raket,
    vp,
    top7,
];

export const VXY_GAMES = [V85, V75, V86, GS75, V64, V65, V5, V4, V3];

export const DIVISION_GAMES = [V85, V75, V86, GS75, V64, V65, V5, V4, V3, dd, ld];

export const COMBINATION_GAMES = [dd, ld, trio, komb, tvilling];

export const DOUBLE_GAMES = [dd, ld];

export const SINGLE_RACE_GAMES = [trio, tvilling, komb, vinnare, plats, vp, top7];

export const SHARED_BET_GAMES = [
    V85,
    V75,
    V86,
    GS75,
    V64,
    V65,
    V5,
    V4,
    dd,
    ld,
    trio,
    komb,
    tvilling,
    top7,
];

// The main sorting order of game types at atg.se
export const gameTypePriorityOrder = {
    [V85]: 1,
    [V75]: 2,
    [V86]: 3,
    [GS75]: 4,
    [V64]: 5,
    [V65]: 6,
    [dd]: 7,
    [ld]: 8,
    [V5]: 9,
    [V4]: 10,
    [V3]: 11,
    [vinnare]: 12,
    [plats]: 13,
    [tvilling]: 14,
    [komb]: 15,
    [trio]: 16,
    [top7]: 17,
    [vp]: 18,
    [raket]: 19,
    H2H: 20,
};

// Only used in calendar where some special logic is applied
export const gameTypePriorityOrderMobile = {
    [V85]: 1,
    [V75]: 2,
    [V86]: 3,
    [GS75]: 4,
    [V64]: 5,
    [V65]: 6,
    [dd]: 7,
    [ld]: 8,
    [V4]: 9,
    [V5]: 10,
    [V3]: 11,
    [vinnare]: 12,
    [plats]: 13,
    [tvilling]: 14,
    [komb]: 15,
    [trio]: 16,
    [top7]: 17,
    [vp]: 18,
    [raket]: 19,
};

export function isHeadToHeadOfferingType(
    gameType: string | undefined,
): gameType is HeadToHeadOfferingType {
    return gameType?.toUpperCase() === "H2H";
}

/**
 * Guard checking if string is a GameType
 */
export function isGameType(value?: string): value is GameType {
    return includes(ALL_GAMES, value) || isHeadToHeadOfferingType(value);
}

export const isDivisionGameType = (
    gameType: GameType | HeadToHeadOfferingType,
): gameType is DivisionGameType => includes(DIVISION_GAMES, gameType);

export const isSingleRaceGameType = (
    gameType: GameType | HeadToHeadOfferingType,
): gameType is SingleRaceGameType => includes(SINGLE_RACE_GAMES, gameType);

export const isCombinationGameType = (
    gameType: GameType | HeadToHeadOfferingType,
): gameType is ComboGameType => includes(COMBINATION_GAMES, gameType);

export const isDoubleGameType = (
    gameType: GameType | HeadToHeadOfferingType,
): gameType is DubbelGameType => includes(DOUBLE_GAMES, gameType);

export const isVXYGameType = (
    gameType: GameType | HeadToHeadOfferingType,
): gameType is VXYGameType => includes(VXY_GAMES, gameType);

export const isSharedBetGameType = (
    gameType: GameType | HeadToHeadOfferingType,
): gameType is SharedBetGameType => includes(SHARED_BET_GAMES, gameType);

export const getGameTypeFromString = (gameType?: string): GameType | undefined =>
    isGameType(gameType) ? GameTypes[gameType] : undefined;

export const getGameType = (gameType: string): GameType | undefined =>
    isGameType(gameType) ? GameTypes[gameType] : undefined;

export const getGameTypeFromStringCaseInsensitive = (
    gameType?: string | null,
): GameType | undefined => {
    if (!gameType) return undefined;
    const upperCaseGameType = gameType.toUpperCase();
    const lowerCaseGameType = gameType.toLowerCase();

    if (isGameType(upperCaseGameType)) {
        return GameTypes[upperCaseGameType];
    }

    if (isGameType(lowerCaseGameType)) {
        return GameTypes[lowerCaseGameType];
    }

    // If neither version is valid, return undefined
    return undefined;
};

/**
 * Determines if the given game type is a primary game type.
 *
 * @param gameType - The game type to check.
 * @returns `true` if the game type is V85, V75, V86, GS75, V64, V65, otherwise `false`.
 */
export const isPrimaryGameType = (gameType: GameType | undefined): boolean => {
    if (!gameType) return false;
    return (gameTypePriorityOrder[gameType] || 99) < gameTypePriorityOrder[dd];
};
